import { uploadToCloudinary } from "./Cloudinary";
import { db } from "../firebase";
import { doc, setDoc, getDoc } from "firebase/firestore";

/**
 * Uploads an image to Cloudinary and saves the URL to Firestore.
 * @param {File} file - The image file to upload.
 * @param {string} section - The section name (e.g., "Hero", "Explore India").
 * @param {string} cloudName - Cloudinary cloud name.
 * @param {string} uploadPreset - Cloudinary upload preset.
 * @returns {Promise<string>} - The Cloudinary URL.
 */
export const uploadAndSaveImage = async (file, section, cloudName, uploadPreset) => {
  // Folder name as section, filename as timestamp
  const folder = section;
  const timestamp = Date.now();
  const fileName = `${timestamp}_${file.name}`;

  // Cloudinary supports public_id for custom file names
  const url = `https://api.cloudinary.com/v1_1/${cloudName}/upload`;
  const formData = new FormData();
  formData.append('file', file);
  formData.append('upload_preset', uploadPreset);
  formData.append('folder', folder);
  formData.append('public_id', fileName);

  // Upload to Cloudinary
  let imageUrl;
  try {
    const response = await fetch(url, {
      method: 'POST',
      body: formData,
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Cloudinary upload failed: ${errorData.error.message}`);
    }
    const data = await response.json();
    imageUrl = data.secure_url;
  } catch (error) {
    // console.error("Error uploading to Cloudinary:", error);
    throw error;
  }

  // Save to Firestore: Images > AllImages > {section}
  try {
    const docRef = doc(db, "Images", "AllImages");
    const docSnap = await getDoc(docRef);
    let imagesData = {};
    if (docSnap.exists()) {
      imagesData = docSnap.data();
    }
    // Save as { section: { url, updatedAt } }
    await setDoc(docRef, {
      ...imagesData,
      [section]: {
        url: imageUrl,
        updatedAt: new Date().toISOString(),
        fileName,
      }
    });
  } catch (error) {
    // console.error("Error saving image URL to Firestore:", error);
    throw error;
  }

  return imageUrl;
};
